import { LabelAnnotation } from "@/features/report/models";
import { utils } from "@/globals/utils";
import { gConsts } from "@/globals/gConsts";
import localizacaoInternaJson from "./resources/mapeamento_localizacao_interna.json";
import localizacaoExternaJson from "./resources/mapeamento_localizacao_externa.json";
import labelsJson from "./resources/mapeamento_labels.json";
import qualificadoresJson from "./resources/mapeamento_qualificadores.json";
import { ESPECIFICACAO_GRUPO_TIPO_TO_LABEL, ESPECIFICACAO_TO_LABEL } from "./resources/mappers/EspecificacaoMapper";
import { TIPO_ANOTACAO_TO_TIPO_ASSOCIACAO } from "./resources/mappers/AnotacaoMapper";

export const anotacaoUtils = {

    /**
     * @param {Array<Tool>} tools 
     * @param {LabelAnnotation} annotation 
     * @returns {Array<Element>}
     */
    getLocationToolElementsFromAnnotation(tools, annotation) {
        const toolElements = [];
        annotation.localizacao.dentes.forEach(dente => {
            const toolElement = utils.findToolElementByLabelIdAndToolType(document, tools, dente.id, gConsts.tool.attrib.types.CURVA);
            if (toolElement) {
                toolElements.push(toolElement);
            }
        });
        annotation.localizacao.regioesAnatomicas.forEach(regiao => {
            const toolElement = utils.findToolElementByLabelIdAndToolType(document, tools, regiao.id, gConsts.tool.attrib.types.PATH);
            if (toolElement) {
                toolElements.push(toolElement);
            }
        });
        return toolElements;
    },

    getEspecificacoesByLabelId(labelId) {
        const listaEspecificacoes = [];
        const localizacaoExternaInfo = this.getLocalizacaoExternaInfoByLabelId(labelId);
        if (localizacaoExternaInfo && localizacaoExternaInfo.especificacoes?.length > 0) {
            listaEspecificacoes.push(...localizacaoExternaInfo.especificacoes);
        }
        return listaEspecificacoes;
    },

    getEspecificacaoTipoGrupoByLabelId(labelId) {
        const especificacoes = this.getEspecificacoesByLabelId(labelId);
        if (!especificacoes) {
            return null;
        }
        const especificacaoesComGrupos = especificacoes.filter(especificacao => especificacao?.split('__')?.length > 1);
        if (!especificacaoesComGrupos.length) {
            return null;
        }
        const tipoGrupo = especificacaoesComGrupos.at(0).split('__')[0];
        return tipoGrupo;
    },

    getEspecificacaoGrupoByLabelId(labelId) {
        const especificacoes = this.getEspecificacoesByLabelId(labelId);
        if (!especificacoes) {
            return null;
        }
        const hasGrupo = especificacoes.some(especificacao => especificacao?.split('__')?.length > 1);
        if (!hasGrupo) {
            return null;
        }
        const tipoGrupo = especificacoes[0].split('__')[0];
        return {
            nome: ESPECIFICACAO_GRUPO_TIPO_TO_LABEL[tipoGrupo],
            tipo: tipoGrupo,
            items: especificacoes.map(especificacao => {
                const [grupo, nomeEspecificacao] = especificacao.split('__');
                return {
                    value: especificacao,
                    label: ESPECIFICACAO_TO_LABEL[nomeEspecificacao],
                }
            }),
        };
    },

    /**
     * @param {string} labelId 
     * @returns {{
     *  tipo_anotacao: string;
     *  localizacao: {
     *    face: {
     *      lista: string[];
     *      tipo: string;
     *    };
     *    porcao: {
     *      lista: string[];
     *      tipo: string;
     *    };
     *    especificacao: string;
     *  };
     * } | null}
     */
    getLocalizacaoInternaInfoByLabelId(labelId) {
        return localizacaoInternaJson[labelId] || null;
    },

    /**
     * @param {string} labelId 
     * @returns {Array<{
     *  label: string;
     *  type: string;
     *  format: 'SAME_LINE' | 'NEW_LINE';
     *  text: string;
     * }>}
     */
    getQualificadoresByLabelId(labelId) {
        return qualificadoresJson[labelId] || [];
    },

    /**
     * @param {string} labelId 
     * @returns {{
     *  tipo_anotacao: string;
     *  especificacoes: string[];
     * } | null}
     */
    getLocalizacaoExternaInfoByLabelId(labelId) {
        return localizacaoExternaJson[labelId] || null;
    },

    /**
     * @param {string} labelId 
     * @returns {{
     *  id: string;
     *  annotation: {
     *    type: string;
     *  };
     *  layout: {
     *    label: string;
     *    caption: string;
     *  };
     *  report: {
     *    text: string;
     *  };
     * } | null}
     */
    getLabelInfoByLabelId(labelId) {
        const items = labelsJson[labelId] || [];
        return items[0] || null;
    },

    getLabelAnnotationTypeById(labelId) {
        if (!labelId) {
            return null;
        }
        const localizacaoInternaInfo = this.getLocalizacaoInternaInfoByLabelId(labelId);
        if (localizacaoInternaInfo) {
            return localizacaoInternaInfo.tipo_anotacao?.toLowerCase();
        }
        const localizacaoExternaInfo = this.getLocalizacaoExternaInfoByLabelId(labelId);
        if (localizacaoExternaInfo) {
            return localizacaoExternaInfo.tipo_anotacao?.toLowerCase();
        }

        const labelInfo = this.getLabelInfoByLabelId(labelId);
        if (labelInfo) {
            return labelInfo.annotation.type?.toLowerCase();
        }
        return null;
    },

    getAssociationTypeByLabelId(labelId) {
        const labelAnnotationType = this.getLabelAnnotationTypeById(labelId);
        return TIPO_ANOTACAO_TO_TIPO_ASSOCIACAO[labelAnnotationType] || gConsts.node.report.annotation.association.INDIVIDUAL;
    },

    getAssociationTypeByAnnotationType(annotationType) {
        return TIPO_ANOTACAO_TO_TIPO_ASSOCIACAO[annotationType] || gConsts.node.report.annotation.association.INDIVIDUAL;
    },

    labelHasAnnotations(labelId) {
        const labelAnnotationType = this.getLabelAnnotationTypeById(labelId);
        return labelAnnotationType && labelAnnotationType != gConsts.node.report.annotation.types.FIXED;
    },

    /**
     * Se o label tem algo na anotacao dele para ser editado
     * @param {string} labelId 
     * @returns {boolean}
     */
    canLabelEditAnnotations(labelId) {
        if (!labelId)
            return false;
        if (!this.labelHasAnnotations(labelId))
            return false;

        return true;

        // const localizacaoInternaInfo = this.getLocalizacaoInternaInfoByLabelId(labelId);
        // if (!!localizacaoInternaInfo && (
        //     localizacaoInternaInfo.localizacao.face.lista.length > 0 ||
        //     localizacaoInternaInfo.localizacao.porcao.lista.length > 0
        // ))
        //     return true;
        // const qualificadoresLabel = this.getQualificadoresByLabelId(labelId);
        // if (!!qualificadoresLabel && qualificadoresLabel.length > 0)
        //     return true;
        // const especificacoesLabel = this.getEspecificacoesByLabelId(labelId);
        // if (!!especificacoesLabel && especificacoesLabel.length > 0)
        //     return true;

        // return false;
    },

    especificacaoPermiteDentes(especificacao) {
        return !['regiao_palatina'].some(e => especificacao?.endsWith(e));
    }

}